/**
 * OrderLineEditor — controlled line-items table for the admin "New Order"
 * (telesales desk) page (`OrderCreate.tsx`, Task 5).
 *
 * Mirrors `routes/quotes/QuoteItemsEditor.tsx`'s controlled-table shape
 * (rows table → qty input + price-override input + remove-row + add-line)
 * but is deliberately DUMBER: it computes NO totals. The spec mandates
 * "never client math" — every priced figure (unit price, VAT, line total)
 * comes from the server `POST /v1/admin/orders/preview` response and is
 * passed back in via the `priced` prop, keyed by the row index. Until a
 * preview resolves for a row, the priced columns render "—".
 *
 * The parent (`OrderCreate`) owns:
 *   - the product search → product detail → variant pick flow (the
 *     "add line" affordance lives there, since it needs to fetch).
 *   - the rows array + the latest preview result.
 * This editor just renders rows, edits qty/override, and removes rows.
 */
import {
  Button,
  Input,
  Numeric,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@wmart/ui";
import { formatGBP } from "../lib/format.js";

/**
 * One editable order line. `variant_id` + the display labels are resolved
 * by the parent at add-time (product detail fetch). `quantity` and
 * `unit_price_override_minor` are free text the operator edits.
 */
export interface OrderLineRow {
  variant_id: string;
  /** Free-text qty (kept as string so the input is fully controlled). */
  quantity: string;
  /** Free-text ex-VAT net override in pence; "" = use the resolved tier price. */
  unit_price_override_minor: string;
  /** Display-only, resolved by the parent at add-time. */
  product_title: string;
  variant_title: string;
  sku: string;
}

/**
 * Server-resolved priced figures for a single line, taken from the latest
 * preview response. `null` when no preview has resolved for this row yet
 * (the priced columns then render "—").
 */
export interface PricedLine {
  unit_price_minor: number;
  vat_amount_minor: number;
  line_total_minor: number;
  overridden: boolean;
}

export interface OrderLineEditorProps {
  rows: OrderLineRow[];
  /** Priced figures per row index (from the server preview). */
  priced: Array<PricedLine | null>;
  onChange: (next: OrderLineRow[]) => void;
  disabled?: boolean;
}

export function OrderLineEditor({
  rows,
  priced,
  onChange,
  disabled = false,
}: OrderLineEditorProps) {
  function updateRow(idx: number, patch: Partial<OrderLineRow>) {
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removeRow(idx: number) {
    onChange(rows.filter((_, i) => i !== idx));
  }

  if (rows.length === 0) {
    return (
      <p className="text-body-sm text-ink-500">
        No lines yet — search for a product above to add one.
      </p>
    );
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Product</TH>
          <TH className="text-right">Qty</TH>
          <TH className="text-right">Override (pence)</TH>
          <TH className="text-right">Unit price</TH>
          <TH className="text-right">VAT</TH>
          <TH className="text-right">Line total</TH>
          {!disabled ? <TH>{/* remove */}</TH> : null}
        </TR>
      </THead>
      <TBody>
        {rows.map((row, idx) => {
          const p = priced[idx] ?? null;
          return (
            <TR key={`line-${idx}-${row.variant_id}`}>
              <TD>
                <div className="flex flex-col gap-0.5">
                  <span className="text-body-sm font-medium text-ink-900">
                    {row.product_title}
                  </span>
                  <span className="text-caption text-ink-500">
                    {row.variant_title}
                    {row.sku ? ` · ${row.sku}` : ""}
                  </span>
                </div>
              </TD>
              <TD className="text-right">
                {disabled ? (
                  <Numeric>{row.quantity}</Numeric>
                ) : (
                  <Input
                    aria-label={`Quantity for line ${idx + 1}`}
                    type="text"
                    inputMode="numeric"
                    value={row.quantity}
                    onChange={(e) =>
                      updateRow(idx, { quantity: e.target.value })
                    }
                    className="text-right"
                  />
                )}
              </TD>
              <TD className="text-right">
                {disabled ? (
                  <Numeric>
                    {row.unit_price_override_minor === ""
                      ? "—"
                      : row.unit_price_override_minor}
                  </Numeric>
                ) : (
                  <Input
                    aria-label={`Unit price override for line ${idx + 1}`}
                    type="text"
                    inputMode="numeric"
                    placeholder="use tier price"
                    value={row.unit_price_override_minor}
                    onChange={(e) =>
                      updateRow(idx, {
                        unit_price_override_minor: e.target.value,
                      })
                    }
                    className="text-right"
                  />
                )}
              </TD>
              <TD className="text-right text-body-sm text-ink-700">
                {p ? (
                  <Numeric>
                    {formatGBP(p.unit_price_minor)}
                    {p.overridden ? " *" : ""}
                  </Numeric>
                ) : (
                  <span className="text-ink-500">—</span>
                )}
              </TD>
              <TD className="text-right text-body-sm text-ink-700">
                {p ? (
                  <Numeric>{formatGBP(p.vat_amount_minor)}</Numeric>
                ) : (
                  <span className="text-ink-500">—</span>
                )}
              </TD>
              <TD className="text-right">
                {p ? (
                  <Numeric serif>{formatGBP(p.line_total_minor)}</Numeric>
                ) : (
                  <span className="text-ink-500">—</span>
                )}
              </TD>
              {!disabled ? (
                <TD>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRow(idx)}
                    aria-label={`Remove line ${idx + 1}`}
                  >
                    Remove
                  </Button>
                </TD>
              ) : null}
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}
